export const OFFICIAL_PRODUCTION_HOSTS = Object.freeze([
  'cassavachain.com',
  'www.cassavachain.com',
  'buyer.cassavachain.com',
]);

export const OFFICIAL_PLATFORM_URL = 'https://www.cassavachain.com/';
export const OFFICIAL_BUYER_PORTAL_URL = 'https://buyer.cassavachain.com/';

const DEV_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const LOCAL_PREVIEW_HOSTS = new Set(['localhost', '127.0.0.1']);
const OFFICIAL_PRODUCTION_HOST_SET = new Set(OFFICIAL_PRODUCTION_HOSTS);
const ROOT_ENTRY_LOCAL_PREVIEW_LAB_ENTRY_SOURCES = new Set([
  'googleredirectstartlab',
  'googleredirectreturnlab',
  'googlepopuplab',
  'actiondispatcherlab',
  'publiclegalparity',
  'rootruntimepreview',
  'errorboundarylab',
  'workspacelazyproof',
  'runtime',
  'firebaseauth',
  'accesssnapshot',
]);

function parseRootEntryTestHosts(rawHosts = '') {
  return String(rawHosts || '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter((host) => {
      if (!host) return false;
      if (host.includes('*')) return false;
      if (host.includes('://')) return false;
      if (host.includes('/')) return false;
      if (host.includes(':')) return false;
      return true;
    });
}

export function isCassavaChainLocalPreviewEnabled() {
  return import.meta.env?.VITE_CASSAVACHAIN_LOCAL_PREVIEW === '1';
}

function isCassavaChainLocalPreviewEnvEnabled(env = import.meta.env) {
  return env?.VITE_CASSAVACHAIN_LOCAL_PREVIEW === '1';
}

function isRootEntryMockEnabled(env = import.meta.env) {
  return env?.VITE_CASSAVACHAIN_ROOT_ENTRY_MOCK === '1';
}

export function normalizeHostname(hostname) {
  const raw = String(hostname || '').toLowerCase().trim();
  if (!raw) return '';
  if (raw.startsWith('[')) {
    const bracketEnd = raw.indexOf(']');
    return bracketEnd > 0 ? raw.slice(1, bracketEnd) : raw.replace(/^\[/, '');
  }
  const colonCount = (raw.match(/:/g) || []).length;
  if (colonCount === 1) return raw.replace(/:\d+$/, '');
  return raw;
}

export function isLocalNetworkHost(hostname) {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return false;
  if (DEV_HOSTS.has(normalized) || normalized.endsWith('.local')) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(normalized)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(normalized)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(normalized)) return true;
  return false;
}

function readEntrySource(search = '') {
  try {
    return String(new URLSearchParams(String(search || '')).get('entrySource') || '')
      .trim()
      .toLowerCase();
  } catch {
    return '';
  }
}

export function isRootEntryLocalPreviewLabHostAllowed({ hostname, search = '', env = import.meta.env } = {}) {
  if (env?.VITE_CASSAVACHAIN_LOCAL_PREVIEW !== '1') return false;
  if (!isRootEntryMockEnabled(env)) return false;
  const normalized = normalizeHostname(hostname);
  const testHosts = new Set(parseRootEntryTestHosts(env?.VITE_CASSAVACHAIN_ROOT_ENTRY_TEST_HOSTS));
  if (!isLocalNetworkHost(normalized) && !testHosts.has(normalized)) return false;
  return ROOT_ENTRY_LOCAL_PREVIEW_LAB_ENTRY_SOURCES.has(readEntrySource(search));
}

export function isOfficialCassavaChainHost(hostname, mode = 'production', options = {}) {
  const normalized = normalizeHostname(hostname);
  if (OFFICIAL_PRODUCTION_HOST_SET.has(normalized)) return true;

  if (isCassavaChainLocalPreviewEnvEnabled(options.env) && LOCAL_PREVIEW_HOSTS.has(normalized)) return true;

  if (isRootEntryLocalPreviewLabHostAllowed({ hostname: normalized, search: options.search, env: options.env })) {
    return true;
  }

  if (mode !== 'production' && isLocalNetworkHost(normalized)) return true;

  return false;
}

export function getCurrentHostname() {
  if (typeof window === 'undefined') return '';
  return normalizeHostname(window.location?.hostname || '');
}
